import React, { useState, useMemo, useRef } from 'react';
import { useReceiptsManager } from '../hooks/useReceiptsManager';
import { useSettings } from '../hooks/useSettings';
import { ReceiptsTable } from './ReceiptsTable';
import { Pagination } from './Pagination';
import { Spinner } from './Spinner';
import { FilterIcon } from './icons/FilterIcon';
import { EditIcon } from './icons/EditIcon';
import { ViewIcon } from './icons/ViewIcon';
import { ExportIcon } from './icons/ExportIcon';
import { ImportIcon } from './icons/ImportIcon';
import { InfoIcon } from './icons/InfoIcon';
import { BulkActionToolbar } from './BulkActionToolbar';
import { ConfirmModal } from './ConfirmModal';
import { CSVInfoModal } from './CSVInfoModal';
import { FileUpload } from './FileUpload';
import { UploadIcon } from './icons/UploadIcon';
import { CameraIcon } from './icons/CameraIcon';
import { ScannerModal } from './ScannerModal';
import { FilterPanel, FiltersState } from './FilterPanel';
import { CloseIcon } from './icons/CloseIcon';

interface ReceiptsViewProps {
    receiptsManager: ReturnType<typeof useReceiptsManager>;
}

export const ReceiptsView: React.FC<ReceiptsViewProps> = ({ receiptsManager }) => {
    const {
        receipts,
        isProcessing,
        isLoading,
        error,
        isEditMode,
        setIsEditMode,
        setIsExportModalOpen,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        totalPages,
        uploadProgress,
        failedUploads,
        retryFailedUploads,
        sortConfig,
        selectedUploadTags,
        setSelectedUploadTags,
        filters,
        setFilters,
        filterOptions,
        selectedReceiptIds,
        handleToggleSelect,
        handleToggleSelectAll,
        handleBulkDelete,
        handleBulkUpdate,
        handleFilesSelected,
        handleUpdateReceipt,
        handleDeleteReceipt,
        handleFileImport,
        handleSort,
        totalReceipts,
        isSelectAllActive,
        handleSelectAllFiltered,
        handleClearSelection,
        receiptsSummary,
        handleClearAllFilters,
    } = receiptsManager;

    const settings = useSettings();
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
    const [isCsvInfoModalOpen, setIsCsvInfoModalOpen] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);
    const uploadInputRef = useRef<HTMLInputElement>(null);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        count += filters.tags.include.length + filters.tags.exclude.length;
        count += filters.businessNames.include.length + filters.businessNames.exclude.length;
        count += filters.categories.include.length + filters.categories.exclude.length;
        count += filters.paymentMethods.include.length + filters.paymentMethods.exclude.length;
        count += filters.currencies.include.length + filters.currencies.exclude.length;
        return count;
    }, [filters]);
    
    const handleApplyFilters = (newFilters: FiltersState) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters
        }));
    };

    const onImportChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileImport(file);
        }
        if (event.target) {
            event.target.value = '';
        }
    };
    
    const onUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleFilesSelected(Array.from(files));
        }
        if (event.target) {
            event.target.value = '';
        }
    };

    const handleBulkDeleteClick = () => {
        if (selectedReceiptIds.size > 0) {
            setIsBulkDeleteConfirmOpen(true);
        }
    };

    const handleConfirmBulkDelete = async () => {
        await handleBulkDelete();
        setIsBulkDeleteConfirmOpen(false);
    };

    const handleScanComplete = (pdfBlob: Blob) => {
        const pdfFile = new File([pdfBlob], `sken_${new Date().toISOString()}.pdf`, { type: 'application/pdf' });
        handleFilesSelected([pdfFile]);
        setIsScannerOpen(false);
    };
    
    const areAllOnPageSelected = receipts.length > 0 && receipts.every(r => selectedReceiptIds.has(r.id));
    const showSelectAllFilteredBanner = areAllOnPageSelected && !isSelectAllActive && totalReceipts > receipts.length;

    return (
        <>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                    <input
                        type="text"
                        placeholder="Hledat v účtenkách..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="p-2 border border-gray-300 rounded-md w-full sm:w-48"
                    />
                    <FileUpload
                        selectedTags={selectedUploadTags}
                        onSelectedTagsChange={setSelectedUploadTags}
                        allTags={settings.tags}
                        onAddTag={(item) => settings.addItem('tags', item)}
                    />
                </div>

                <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end w-full sm:w-auto">
                    <input
                        type="file"
                        ref={uploadInputRef}
                        multiple
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={onUploadChange}
                    />
                     <button
                        onClick={() => setIsScannerOpen(true)}
                        className="p-2 rounded-md bg-indigo-500 text-white hover:bg-indigo-600 transition-colors flex items-center gap-2"
                        title="Skenovat účtenku mobilem"
                    >
                        <CameraIcon className="w-5 h-5" /> Skenovat
                    </button>
                    <button
                        onClick={() => uploadInputRef.current?.click()}
                        className="p-2 rounded-md bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
                        title="Nahrát nové účtenky"
                    >
                        <UploadIcon className="w-5 h-5" /> Nahrát
                    </button>
                    
                    <input type="file" ref={importInputRef} className="hidden" accept=".csv" onChange={onImportChange} />
                    <div className="flex">
                        <button onClick={() => importInputRef.current?.click()} className="p-2 rounded-l-md bg-teal-500 text-white hover:bg-teal-600 transition-colors flex items-center gap-2" title="Importovat z CSV">
                            <ImportIcon className="w-5 h-5"/> Importovat
                        </button>
                        <button onClick={() => setIsCsvInfoModalOpen(true)} className="p-2 rounded-r-md bg-teal-600 text-white hover:bg-teal-700 transition-colors border-l border-teal-500 flex items-center" title="Informace o formátu CSV">
                            <InfoIcon className="w-6 h-6"/>
                        </button>
                    </div>
                    <button onClick={() => setIsExportModalOpen(true)} className="p-2 rounded-md bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-2" title="Exportovat do CSV">
                        <ExportIcon className="w-5 h-5"/> Exportovat
                    </button>
                    <button onClick={() => setIsEditMode(!isEditMode)} className="p-2 rounded-md bg-purple-500 text-white hover:bg-purple-600 transition-colors" title={isEditMode ? "Ukončit úpravy" : "Upravit tabulku"}>
                        {isEditMode ? <ViewIcon className="w-5 h-5"/> : <EditIcon className="w-5 h-5"/>}
                    </button>
                </div>
            </div>

            <div className="mb-4">
                <div className="flex justify-between items-center flex-wrap gap-2 mb-2">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsFilterPanelOpen(true)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-900 font-medium">
                            <FilterIcon className="w-4 h-4" />
                            Filtrovat
                            {activeFilterCount > 0 && (
                                <span className="ml-1 bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{activeFilterCount}</span>
                            )}
                        </button>
                        {activeFilterCount > 0 && (
                            <button onClick={handleClearAllFilters} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-medium" title="Vymazat všechny filtry">
                                <CloseIcon className="w-4 h-4" />
                                <span>Vymazat</span>
                            </button>
                        )}
                    </div>
                    {receiptsSummary && receiptsSummary.length > 0 && (
                        <div className="text-sm text-right">
                            <span className="font-semibold text-gray-600">Celkový součet (filtr): </span>
                            {receiptsSummary.map((s, index) => (
                                <span key={s.currency} className="font-bold text-gray-800 ml-1">
                                    {(s.total ?? 0).toLocaleString('cs-CZ', { style: 'currency', currency: s.currency || 'CZK' })}
                                    {index < receiptsSummary.length - 1 && ' + '}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {(isProcessing || error || failedUploads.length > 0) && (
              <div className="my-4">
                {isProcessing && <Spinner text={`Zpracovávám...`} progress={uploadProgress || undefined} />}
                {error && <div className="mt-4 text-red-600 bg-red-100 p-3 rounded-md">{error}</div>}
                {!isProcessing && failedUploads.length > 0 && (
                    <div className="mt-4 p-4 bg-orange-100 rounded-md">
                        <h3 className="font-semibold text-orange-800">Následující soubory se nepodařilo zpracovat:</h3>
                        <ul className="list-disc list-inside mt-2 text-sm text-orange-700">
                            {failedUploads.map(({ file, error }, index) => (
                                <li key={index}><strong>{file.name}</strong> - Důvod: {error}</li>
                            ))}
                        </ul>
                         <button
                            onClick={retryFailedUploads}
                            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
                        >
                            Zkusit znovu ({failedUploads.length})
                        </button>
                    </div>
                )}
              </div>
            )}
            
            <div className={`transition-opacity duration-300 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
                {isEditMode && selectedReceiptIds.size > 0 && (
                    <BulkActionToolbar
                        selectedCount={selectedReceiptIds.size}
                        onBulkDelete={handleBulkDeleteClick}
                        onBulkUpdate={handleBulkUpdate}
                        onClearSelection={handleClearSelection}
                        totalFilteredCount={totalReceipts}
                        isSelectAllActive={isSelectAllActive}
                        onSelectAllFiltered={handleSelectAllFiltered}
                        showSelectAllFilteredBanner={showSelectAllFilteredBanner}
                    />
                )}

                <ReceiptsTable
                    receipts={receipts}
                    isEditMode={isEditMode}
                    onUpdate={handleUpdateReceipt}
                    onDelete={handleDeleteReceipt}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    selectedReceiptIds={selectedReceiptIds}
                    onToggleSelect={handleToggleSelect}
                    onToggleSelectAll={handleToggleSelectAll}
                />
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
             <FilterPanel
                isOpen={isFilterPanelOpen}
                onClose={() => setIsFilterPanelOpen(false)}
                onApply={handleApplyFilters}
                initialFilters={{
                    tags: filters.tags,
                    businessNames: filters.businessNames,
                    categories: filters.categories,
                    paymentMethods: filters.paymentMethods,
                    currencies: filters.currencies,
                }}
                filterOptions={filterOptions}
            />

            <ConfirmModal
                isOpen={isBulkDeleteConfirmOpen}
                onClose={() => setIsBulkDeleteConfirmOpen(false)}
                onConfirm={handleConfirmBulkDelete}
                title={`Smazat ${selectedReceiptIds.size} účtenek?`}
                confirmText="Smazat"
                confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
            >
                <p>Opravdu si přejete smazat vybrané účtenky? Tato akce je nevratná.</p>
            </ConfirmModal>
            
            {isCsvInfoModalOpen && (
                <CSVInfoModal isOpen={isCsvInfoModalOpen} onClose={() => setIsCsvInfoModalOpen(false)} />
            )}
            
            {isScannerOpen && (
                <ScannerModal
                    isOpen={isScannerOpen}
                    onClose={() => setIsScannerOpen(false)}
                    onScanComplete={handleScanComplete}
                />
            )}
        </>
    );
};